import React, { useState } from "react";
import { Col, OverlayTrigger, Row, Toast, ToastContainer, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import DstButton from "../../../../components/DstButton/DstButton";
import DstConfirm from "../../../../components/DstConfirm/DstConfirm";
import DstTable from "../../../../components/DstTable/DstTable";
import InfoRow from "../../../../components/InfoRow/InfoRow";
import MultiSelectLiveDropdown from "../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import { useSession } from "../../../../contexts/SessionContext";
import { PermissionComponent } from "../../../../services/PermissionGuard";
import { formatDateHumanly, formatHourRange } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import { addModulesToProjectMembers, deleteProject, removeUsersFromProject } from "../../Project.function";
import AddUsersModal from "./AddUserModal/AddUserModal";
import CreateMeetingModal from "./CreateMeetingModal/CreateMeetingModal";
import EditProjectModal from "./EditProjectModal/EditProjectModal";
import ProjectResources from "./ProjectRessources/ProjectRessources";
import UpdateScoreModal from "./UpdateScoreModal/UpdateScoreModal";

const MainInfo = ({
  project,
  meetings,
  loadingMeetings,
  users,
  loadingUsers,
  refreshUsers,
  refreshMeetings,
  onProjectEditSuccess,
}: any) => {
  const { language } = useSession();
  const navigate = useNavigate();
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showCreationModal, setShowCreationModal] = useState(false);
  const [usersSelected, setUsersSelected] = useState<any[]>([]);
  const [showUpdateScoreModal, setShowUpdateScoreModal] = useState(false);
  const [loadingRemoveUsers, setLoadingRemoveUsers] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const [loadingAddModules, setLoadingAddModules] = useState(false);
  const [showAddModulesSuccessToast, setShowAddModulesSuccessToast] = useState(false);

  const formattedUsers = users.map((projectUser: any) => ({
    id: projectUser.user.id,
    name: projectUser.user.full_name,
    badges: projectUser.user.flags || [],
    score: projectUser.score,
    score_base: projectUser.score_base,
  }));

  const columnsUsers = [
    {
      key: "name",
      name: translate(language, "GLOBAL.NAME"),
      render: (item: any) => (
        <Link to={`/user/${item.id}`} className="text-decoration-none">
          {item.name}
        </Link>
      ),
    },
    {
      key: "badges",
      name: translate(language, "GLOBAL.FLAGS"),
      stylishCell: () => ({ verticalAlign: "middle" }),
      render: (item: any) => {
        if (!item.badges || item.badges.length === 0) {
          return <span className="text-muted">N/A</span>;
        }

        return (
          <div className="d-flex gap-2 flex-wrap">
            {item.badges.map((badge: any) => (
              <OverlayTrigger
                key={badge.id}
                placement="top"
                overlay={<Tooltip id={`tooltip-badge-${badge.id}`}>{badge.name}</Tooltip>}
              >
                <span className={`dst-icon-${badge.icon_name} blue-font`} />
              </OverlayTrigger>
            ))}
          </div>
        );
      },
    },
    {
      key: "score",
      name: translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEMBERS_COLUMNS.SCORE"),
      render: (item: any) =>
        item.score !== null ? `${item.score}/${item.score_base ?? 20}` : <span className="text-muted">N/A</span>,
    },
  ];

  const columnsMeetings = [
    {
      key: "id",
      name: translate(language, "GLOBAL.ID"),
      render: (item: any) => <Link to={`/meeting/${item.id}`}>{item.id}</Link>,
    },
    {
      key: "name",
      name: translate(language, "GLOBAL.TITLE"),
      render: (item: any) => item.name,
    },
    {
      key: "date",
      name: translate(language, "GLOBAL.DATE"),
      render: (item: any) => formatDateHumanly(language, item.dt_start),
    },
    {
      key: "time",
      name: translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.COLUMNS.TIME"),
      render: (item: any) => formatHourRange(item.dt_start, item.dt_end),
    },
    // {
    //   key: "feedback",
    //   name: translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.COLUMNS.FEEDBACK"),
    //   render: () => (
    //     <span className="blue-font cursor-pointer">
    //       {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.MEETINGS.ADD_FEEDBACK")}
    //     </span>
    //   ),
    // },
  ];

  const _handleRemoveUsers = () => {
    removeUsersFromProject(
      project.id,
      usersSelected.map((user: any) => user.id),
      setLoadingRemoveUsers,
      () => {
        refreshUsers();
        setUsersSelected([]);
      },
      language
    );
  };

  return (
    <React.Fragment>
      <Row>
        <Col lg>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="mb-0 m-bold">
              {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.SECTIONS.INFORMATION")}
            </div>
            <div className="d-flex">
              <PermissionComponent neededPermission={[{ methods: ["DELETE"], route: "/projects/*" }]}>
                <DstButton
                  btnClass="me-2"
                  btnImageBefore={<span className="dst-icon-trash-01 me-2" />}
                  value={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.ACTIONS.DELETE_PROJECT")}
                  variant="danger"
                  clickFunction={() => setShowDeleteProjectModal(true)}
                />
              </PermissionComponent>
              <DstButton
                variant="secondary"
                value={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.ACTIONS.EDIT_PROJECT_INFO")}
                btnImageBefore={<span className="dst-icon-edit-02 me-2" />}
                clickFunction={() => setShowEditProjectModal(true)}
              />
            </div>
          </div>
          <div>
            {InfoRow(
              "dst-icon-clipboard",
              translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.FIELDS.SUBJECT"),
              <Link className="black-font" to={`/projects/subjects/${project.subject.id}`}>
                {project.subject.name}
              </Link>
            )}
            {/* {InfoRow("dst-icon-globe-05", "Language spoken", "English")} */}
            {InfoRow(
              "dst-icon-user-circle",
              translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.FIELDS.OVERSEER"),
              project.overseer?.full_name
            )}
            {InfoRow(
              "dst-icon-user-circle",
              translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.FIELDS.COHORT"),
              project.cohort ? <Link to={`/cohort/${project.cohort.id}`}>{project.cohort.name}</Link> : "—"
            )}
            {InfoRow(
              "dst-icon-calendar-date",
              translate(language, "GLOBAL.CREATED_DATE"),
              formatDateHumanly(language, project.dt_created, true)
            )}
            {InfoRow(
              "dst-icon-calendar-date",
              translate(language, "GLOBAL.START_DATE"),
              formatDateHumanly(language, project.dt_start, true)
            )}
            {InfoRow(
              "dst-icon-calendar-date",
              translate(language, "GLOBAL.END_DATE"),
              formatDateHumanly(language, project.dt_end, true)
            )}
            {InfoRow(
              "dst-icon-link-01",
              translate(language, "GLOBAL.LINK"),
              project.url?.length ? (
                <a href={project.url} target="_blank" rel="noopener noreferrer">
                  {project.url}
                </a>
              ) : null
            )}
            {/* {InfoRow("dst-icon-calendar-date", "Latest Status ", "On time")} */}
            {InfoRow(
              "dst-icon-calendar-date",
              translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.FIELDS.SOUTENANCE_DATE"),
              <i className="orange-font text-decoration-underline clickable" onClick={() => alert("WIP")}>
                {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.ACTIONS.SET_SOUTENANCE_DATE")}
              </i>
            )}
          </div>
          {/* <div className="d-flex justify-content-between align-items-center m-regular mt-4 mb-3">
          <div>{translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.SECTIONS.LATEST_COMMENTS")}</div>
            value={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.ACTIONS.SEE_ALL_COMMENTS")}
        </div>

        <div>
          <div className="s-regular text-muted mb-2">
            <span className="dst-icon-user-circle blue-font me-2" />
            Note du 31/08/25 par Maria Massot (Project mentor)
          </div>
          <div className="rounded border off-white-background s-regular p-3">
            <strong>EXTERNAL COMMENTS</strong> <br /> <strong>What we talked about</strong>: <br /> - Lorem ipsum dolor
            sit amet, consectetur adipiscing elit,
            <br />
            <strong> Next steps</strong>: - sed do eiusmod tempor incididunt ut labore et - dolore magna aliqua. Ut enim
            ad minim veniam,
            <br />
            <strong>Other comments</strong>: <br />- quis nostrud exercitation ullamco laboris nisi ut <br />{" "}
            <strong>INTERNAL COMMENTS</strong> <br />
            <strong>Status</strong> : Late <br />
            Internal comments:- aliquip ex ea commodo consequat..
          </div>
        </div> */}
        </Col>
        <Col>
          <div className="d-flex justify-content-between align-items-center m-regular mb-3">
            <div>
              {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.SECTIONS.MEMBERS")} ({formattedUsers.length})
            </div>

            <div className="d-flex gap-2">
              {usersSelected.length > 0 && (
                <DstButton
                  variant="danger"
                  btnImageBefore={<span className="dst-icon-user-minus-02 me-2" />}
                  value={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.ACTIONS.REMOVE_USERS")}
                  clickFunction={_handleRemoveUsers}
                  loading={loadingRemoveUsers}
                  disabled={loadingRemoveUsers}
                />
              )}
              {usersSelected.length > 0 && (
                <DstButton
                  variant="primary"
                  btnImageBefore={<span className="dst-icon-star-01 me-2" />}
                  value={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.ACTIONS.UPDATE_SCORE")}
                  clickFunction={() => setShowUpdateScoreModal(true)}
                />
              )}
              {usersSelected.length === 0 && (
                <MultiSelectLiveDropdown
                  label={
                    loadingAddModules ? (
                      <React.Fragment>
                        <span className="spinner-border spinner-border-sm me-2" />
                        {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.ACTIONS.ADD_MODULES")}
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        <span className="dst-icon-package-plus me-2 align-content-center blue-font" />
                        <span className="blue-font">
                          {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.ACTIONS.ADD_MODULES")}
                        </span>
                      </React.Fragment>
                    )
                  }
                  category="modules"
                  language={language}
                  hideCaret={true}
                  disabled={loadingAddModules}
                  onConfirm={(selectedModules: any[]) => {
                    if (selectedModules.length === 0) return;

                    addModulesToProjectMembers(
                      project.id,
                      selectedModules.map((module) => module.value.id),
                      language,
                      setLoadingAddModules,
                      () => {
                        setShowAddModulesSuccessToast(true);
                      }
                    );
                  }}
                  clearOnConfirm
                  toggleClass="btn-secondary s-bold"
                  filterCallback={(module: any) => !module.is_deprecated}
                />
              )}
              <DstButton
                variant="secondary"
                value={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.ACTIONS.ADD_USERS")}
                btnImageBefore={<span className="dst-icon-plus me-2" />}
                clickFunction={() => setShowAddUserModal(true)}
              />
            </div>
          </div>
          <DstTable
            selectable
            selectedItems={usersSelected}
            columns={columnsUsers}
            data={formattedUsers}
            loading={loadingUsers}
            onRowSelect={(selectedItems) => {
              setUsersSelected(selectedItems);
            }}
          />

          <div className="d-flex justify-content-between align-items-center m-regular mt-4 my-3">
            <div>{translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.SECTIONS.MEETING_HISTORY")}</div>
            <DstButton
              variant="secondary"
              value={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.ACTIONS.PLAN_MEETING")}
              btnImageBefore={<span className="dst-icon-calendar me-2" />}
              clickFunction={() => setShowCreationModal(true)}
            />
          </div>
          <DstTable columns={columnsMeetings} data={meetings} loading={loadingMeetings} />
          <ProjectResources project={project} language={language} />
        </Col>
      </Row>
      <AddUsersModal
        show={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        projectId={project.id}
        language={language}
        onSuccess={() => {
          refreshUsers();
          setShowAddUserModal(false);
        }}
      />
      <UpdateScoreModal
        show={showUpdateScoreModal}
        onClose={() => setShowUpdateScoreModal(false)}
        projectId={project.id}
        users={usersSelected}
        language={language}
        onSuccess={() => {
          refreshUsers();
          setUsersSelected([]);
        }}
      />
      <EditProjectModal
        show={showEditProjectModal}
        onClose={() => setShowEditProjectModal(false)}
        project={project}
        language={language}
        onSuccess={() => {
          onProjectEditSuccess();
          setShowEditProjectModal(false);
        }}
      />
      <CreateMeetingModal
        language={language}
        setShowCreationModal={setShowCreationModal}
        showCreationModal={showCreationModal}
        projectId={project.id}
        projectName={project.name}
        onSuccess={refreshMeetings}
      />
      <DstConfirm
        show={showDeleteProjectModal}
        onHide={() => setShowDeleteProjectModal(false)}
        onConfirm={() => {
          deleteProject(project.id, language, () => navigate("/projects"));
          setShowDeleteProjectModal(false);
        }}
        title={translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.ACTIONS.DELETE_PROJECT_CONFIRM.TITLE")}
        messageExplain={translate(
          language,
          "PAGES.PROJECT.MAIN_INFOS_TAB.ACTIONS.DELETE_PROJECT_CONFIRM.MESSAGE_EXPLAIN"
        )}
        messageConfirm={translate(
          language,
          "PAGES.PROJECT.MAIN_INFOS_TAB.ACTIONS.DELETE_PROJECT_CONFIRM.MESSAGE_CONFIRM"
        )}
        confirmTextButton={translate(language, "GLOBAL.DELETE")}
      />

      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg="success"
          show={showAddModulesSuccessToast}
          onClose={() => setShowAddModulesSuccessToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">
            {translate(language, "PAGES.PROJECT.MAIN_INFOS_TAB.ACTIONS.MODULES_ADDED_SUCCESS")}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </React.Fragment>
  );
};

export default MainInfo;
